/**
 * A sealed emulator: the wasm next to a `seal.json` that says it is
 * encrypted and where the key comes from.
 *
 * dos.games installs its emulator builds sealed (lib/runtime/seal there):
 * every `emulators/*.wasm` is stored as `<name>.sealed`, AES-256-GCM under a
 * key the page has to ask the site's licence endpoint for, and the endpoint
 * answers only pages on origins it licenses. A copied folder therefore has
 * no key and starts nothing. The protocol is WinEmu's (`docs/Sealing.md` in
 * BoxedWine), so all three engines the site ships speak one dialect.
 *
 * This file is the enDosbox side of it. It costs an unsealed deployment one
 * small request (the manifest probe answers 404 and everything proceeds as
 * before), and it keeps a licence in localStorage so a licensed copy still
 * starts offline for as long as the token the endpoint issued is valid.
 */

interface SealManifest {
    format: string;
    sealId: string;
    licenseUrl: string;
    files: { [name: string]: { sealed: string } };
}

interface License {
    key: string;
    token: string;
    exp: number;
}

const FORMAT = "dosgames-seal/1";
const manifests: { [dir: string]: Promise<SealManifest | null> } = {};
const licenses: { [sealId: string]: Promise<License> } = {};

function dirOf(url: string): string {
    const at = url.lastIndexOf("/");
    return at >= 0 ? url.substring(0, at) : "";
}

function baseOf(url: string): string {
    const at = url.lastIndexOf("/");
    return at >= 0 ? url.substring(at + 1) : url;
}

function stripQuery(url: string): string {
    const at = url.indexOf("?");
    return at >= 0 ? url.substring(0, at) : url;
}

/** The manifest beside a file, or null when the directory is not sealed. */
function manifestFor(dir: string): Promise<SealManifest | null> {
    if (manifests[dir] === undefined) {
        manifests[dir] = fetch(dir + "/seal.json", { cache: "no-store", credentials: "same-origin" })
            .then((r) => (r.ok ? r.text() : ""), () => "")
            .then((text) => {
                // Only a real manifest switches modes: a host that answers
                // every unknown path with its index page must not look sealed.
                try {
                    const seal = text ? JSON.parse(text) : null;
                    if (seal && seal.format === FORMAT && seal.sealId && seal.licenseUrl && seal.files) {
                        return seal as SealManifest;
                    }
                } catch (e) {
                    // not a manifest
                }
                return null;
            });
    }
    return manifests[dir];
}

function storageKey(sealId: string): string {
    return "dosgames-license:" + sealId;
}

function rememberLicense(sealId: string, license: License): void {
    try {
        localStorage.setItem(storageKey(sealId), JSON.stringify(license));
    } catch (e) {
        // private mode or full storage: the licence still lives for this page
    }
}

function keptLicense(sealId: string): License | null {
    try {
        const raw = localStorage.getItem(storageKey(sealId));
        if (!raw) {
            return null;
        }
        const license = JSON.parse(raw) as License;
        if (typeof license.key !== "string" || typeof license.exp !== "number" || license.exp * 1000 < Date.now()) {
            return null;
        }
        return license;
    } catch (e) {
        return null;
    }
}

/** Ask the licence endpoint once per seal; fall back to a licence kept from before when it is unreachable. */
function licenseFor(seal: SealManifest, base: string): Promise<License> {
    if (licenses[seal.sealId] === undefined) {
        const nonce = Array.prototype.map.call(crypto.getRandomValues(new Uint8Array(16)), (b: number) => {
            return ("0" + b.toString(16)).slice(-2);
        }).join("");
        const body = JSON.stringify({ sealId: seal.sealId, origin: location.origin, nonce });
        const url = new URL(seal.licenseUrl, new URL(base, location.href)).toString();
        licenses[seal.sealId] = fetch(url, { method: "POST", headers: { "content-type": "application/json" }, body })
            .then((r) => {
                if (!r.ok) {
                    throw new Error("the licence endpoint answered HTTP " + r.status);
                }
                return r.json();
            }, () => {
                throw new Error("the licence endpoint is unreachable");
            })
            .then((license: any) => {
                if (!license || typeof license.key !== "string" || typeof license.token !== "string") {
                    throw new Error("the licence endpoint returned no key");
                }
                rememberLicense(seal.sealId, license);
                return license as License;
            }, (error: Error) => {
                const kept = keptLicense(seal.sealId);
                if (kept) {
                    console.warn("[SEAL] " + error.message + "; using the licence kept in this browser");
                    return kept;
                }
                delete licenses[seal.sealId];
                throw error;
            });
    }
    return licenses[seal.sealId];
}

function base64ToBytes(text: string): Uint8Array {
    const binary = atob(text);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

async function unseal(sealed: ArrayBuffer, license: License, sealId: string): Promise<ArrayBuffer> {
    if (typeof crypto === "undefined" || !crypto.subtle) {
        throw new Error("a sealed emulator needs a secure context (https or localhost)");
    }
    const bytes = new Uint8Array(sealed);
    if (bytes.length < 12 + 16) {
        throw new Error("the sealed file is truncated");
    }
    const key = await crypto.subtle.importKey("raw", base64ToBytes(license.key), "AES-GCM", false, ["decrypt"]);
    return crypto.subtle.decrypt(
        { name: "AES-GCM", iv: bytes.subarray(0, 12), additionalData: new TextEncoder().encode(sealId), tagLength: 128 },
        key,
        bytes.subarray(12),
    );
}

export interface SealedSource {
    /** Where the encrypted bytes are. */
    url: string;
    /** Turn the fetched bytes into the plain module. */
    open: (sealed: ArrayBuffer) => Promise<ArrayBuffer>;
}

/**
 * Where to fetch a wasm from and how to open it. Null when the file is not
 * sealed, in which case the caller fetches `url` as it always has.
 */
export async function resolveSealed(url: string): Promise<SealedSource | null> {
    if (typeof fetch !== "function" || typeof location === "undefined") {
        return null;
    }
    const plain = stripQuery(url);
    const dir = dirOf(plain);
    const seal = await manifestFor(dir);
    if (seal === null) {
        return null;
    }
    const entry = seal.files[baseOf(plain)];
    if (!entry) {
        return null;
    }
    return {
        url: dir + "/" + entry.sealed,
        open: async (sealed) => {
            let license: License;
            try {
                license = await licenseFor(seal, dir + "/");
            } catch (error: any) {
                const reason = error && error.message ? error.message : String(error);
                console.error("[SEAL] This copy is not licensed for " + location.origin + " (" + reason + ")");
                throw new Error("This copy of the emulator is not licensed for " + location.origin + " (" + reason + ")");
            }
            try {
                return await unseal(sealed, license, seal.sealId);
            } catch (e) {
                throw new Error("The emulator file does not decrypt with the licence this site issued");
            }
        },
    };
}
